import {
  recoveryPoolInnerCall,
  unwrapRecoveryPoolCall,
  wrapRecoveryPoolCall,
} from "./sdk";
import { isPrivateSettlementTarget } from "./privatePools";
import { decodePrivateSwap } from "./privateSwapConfig";
import {
  bridgeDeploymentFor,
  decodePrivateBridge,
} from "./privateBridgeConfig";
import { ethers } from "ethers";
import type { JoinSplitNote } from "../joinsplit/types";
import { assertFlatJoinSplitV2Note, BN254_SCALAR_FIELD } from "./notes";
import { PRIVATE_DEPOSIT_MERGE_INTERFACE } from "./privateFeeDepositMergeAbi";
import {
  depositMergeEvidenceFromJournal,
  depositNoteFromJournal,
} from "./depositEvidence";
import {
  validateSwapOutputSeed,
  type FlatJoinSplitV2SwapOutputSeed,
} from "./swapOutput";

const JOURNAL_SCHEMA_VERSION = 1 as const;
const BIGINT_PREFIX = "__stealthos_bigint:";
export const FLAT_JOINSPLIT_V2_MAX_ACTIVE_JOURNAL_RECORDS = 64;
const STATE_RANK: Record<FlatJoinSplitV2JournalState, number> = {
  prepared: 0,
  preparation_failed: 0,
  input_superseded: 3,
  wallet_rejected: 0,
  broadcast: 1,
  included: 2,
  finalized: 4,
  delivery_pending: 5,
};

export type FlatJoinSplitV2JournalState =
  | "prepared"
  | "preparation_failed"
  | "input_superseded"
  | "wallet_rejected"
  | "broadcast"
  | "included"
  | "finalized"
  | "delivery_pending";

export type FlatJoinSplitV2JournalKind =
  | "deposit"
  | "joinsplit"
  | "withdraw"
  | "withdraw_batch"
  | "migrate"
  | "swap"
  | "bridge";

export interface FlatJoinSplitV2JournalScope {
  chainId: bigint | number;
  poolAddress: string;
  accountAddress: string;
  spendingKey: Uint8Array;
}

export interface FlatJoinSplitV2TransactionIntent {
  to: string;
  from: string;
  data: string;
  value: string;
  chainId: number;
}

export interface FlatJoinSplitV2EncryptedNoteEnvelope {
  ephemeralPubKey: string;
  ciphertext: string;
  iv: string;
  authTag: string;
}

export type FlatJoinSplitV2DeliveryDisposition = "delivered" | "superseded";

/** Evidence that an output was durably delivered by the relayer private-fee
 * recovery backup (confirmed by the relayer for a finalized settlement), so no
 * inbox envelope needs to be staged by this wallet. */
export interface FlatJoinSplitV2DeliveryBackupEvidence {
  kind: "private-fee-recovery";
  quoteId: string;
  txHash: string;
  confirmedAt: number;
}

export interface FlatJoinSplitV2JournalDelivery {
  commitment: string;
  recipientMpkHex: string;
  recipientInboxPublicKeyHex?: string;
  keepLocal: boolean;
  encryptedNote?: FlatJoinSplitV2EncryptedNoteEnvelope;
  durableBackup?: FlatJoinSplitV2DeliveryBackupEvidence;
  disposition?: FlatJoinSplitV2DeliveryDisposition;
  completedAt?: number;
}

export interface FlatJoinSplitV2JournalRecord {
  schemaVersion: typeof JOURNAL_SCHEMA_VERSION;
  id: string;
  state: FlatJoinSplitV2JournalState;
  /** Only a newly built local proof can be undisclosed. Missing/legacy values
   * are conservative, and imported backups always become possibly disclosed. */
  proofDisclosure?: "undisclosed" | "possible";
  inputConflict?: {
    nullifier: string;
    txHash: string;
    blockNumber: number;
    blockHash: string;
  };
  kind: FlatJoinSplitV2JournalKind;
  chainId: number;
  poolAddress: string;
  accountAddress: string;
  transaction: FlatJoinSplitV2TransactionIntent;
  calldataHash: string;
  actionHash?: string;
  sourceCommitments: string[];
  sourceNullifiers: string[];
  outputNotes: JoinSplitNote[];
  deliveries: FlatJoinSplitV2JournalDelivery[];
  swapOutputSeed?: FlatJoinSplitV2SwapOutputSeed;
  swapOutputCommitment?: string;
  txHash?: string;
  blockNumber?: number;
  blockHash?: string;
  createdAt: number;
  updatedAt: number;
}

export interface EncryptedJournalEnvelope {
  schemaVersion: typeof JOURNAL_SCHEMA_VERSION;
  iv: string;
  ciphertext: string;
}

/** Only encrypted prepared snapshots leave the browser. Keys never do. */
export interface FlatJournalBackup {
  id: string;
  chainId: number;
  poolAddress: string;
  accountAddress: string;
  envelope: EncryptedJournalEnvelope;
}

type JournalRecordPayload =
  | {
      type: "record";
      record: FlatJoinSplitV2JournalRecord;
    }
  | {
      type: "deleted";
      id: string;
    };

interface CanonicalScope {
  chainId: number;
  poolAddress: string;
  accountAddress: string;
  spendingKey: Uint8Array;
  storageKey: string;
}

function encodeBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function decodeBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function replacer(_key: string, value: unknown): unknown {
  return typeof value === "bigint" ? `${BIGINT_PREFIX}${value}` : value;
}

function reviver(_key: string, value: unknown): unknown {
  if (typeof value === "string" && value.startsWith(BIGINT_PREFIX)) {
    return BigInt(value.slice(BIGINT_PREFIX.length));
  }
  return value;
}

function canonicalField(value: unknown, label: string): string {
  if (typeof value !== "string" || !ethers.isHexString(value, 32)) {
    throw new Error(`${label} must be exactly 32 bytes`);
  }
  const parsed = BigInt(value);
  if (parsed <= 0n || parsed >= BN254_SCALAR_FIELD) {
    throw new Error(`${label} must be a canonical nonzero BN254 scalar`);
  }
  return ethers.toBeHex(parsed, 32).toLowerCase();
}

function canonicalTxHash(value: string, label: string): string {
  if (!/^0x[0-9a-fA-F]{64}$/.test(value)) {
    throw new Error(`${label} must be a 32-byte transaction hash`);
  }
  return value.toLowerCase();
}

function canonicalHexBytes(
  value: unknown,
  label: string,
  exactBytes?: number,
): string {
  if (typeof value !== "string") {
    throw new Error(`${label} must be hexadecimal bytes`);
  }
  const normalized = value.startsWith("0x") ? value.slice(2) : value;
  if (
    normalized.length === 0 ||
    normalized.length % 2 !== 0 ||
    !/^[0-9a-fA-F]+$/.test(normalized) ||
    (exactBytes !== undefined && normalized.length !== exactBytes * 2)
  ) {
    throw new Error(
      exactBytes === undefined
        ? `${label} must be nonempty hexadecimal bytes`
        : `${label} must be exactly ${exactBytes} bytes`,
    );
  }
  return normalized.toLowerCase();
}

function canonicalDeliveryBackupEvidence(
  value: FlatJoinSplitV2DeliveryBackupEvidence,
): FlatJoinSplitV2DeliveryBackupEvidence {
  if (
    !value ||
    typeof value !== "object" ||
    value.kind !== "private-fee-recovery"
  ) {
    throw new Error(
      "Delivery backup evidence must come from private-fee recovery",
    );
  }
  if (!Number.isSafeInteger(value.confirmedAt) || value.confirmedAt <= 0) {
    throw new Error("Delivery backup evidence needs a confirmation time");
  }
  return {
    kind: "private-fee-recovery",
    quoteId: `0x${canonicalHexBytes(value.quoteId, "backup quote id", 32)}`,
    txHash: canonicalTxHash(value.txHash, "backup transaction hash"),
    confirmedAt: value.confirmedAt,
  };
}

function canonicalEncryptedNoteEnvelope(
  value: FlatJoinSplitV2EncryptedNoteEnvelope,
): FlatJoinSplitV2EncryptedNoteEnvelope {
  if (!value || typeof value !== "object") {
    throw new Error("Encrypted note delivery envelope is invalid");
  }
  const rawEphemeralKey = value.ephemeralPubKey?.trim();
  let ephemeralPubKey: string;
  if (rawEphemeralKey === "inbox-v1") {
    ephemeralPubKey = rawEphemeralKey;
  } else if (rawEphemeralKey?.startsWith("inbox-v2:")) {
    const key = canonicalHexBytes(
      rawEphemeralKey.slice("inbox-v2:".length),
      "Encrypted note ephemeral public key",
      32,
    );
    if (BigInt(`0x${key}`) === 0n) {
      throw new Error("Encrypted note ephemeral public key cannot be zero");
    }
    ephemeralPubKey = `inbox-v2:${key}`;
  } else {
    throw new Error("Encrypted note ephemeral public key version is invalid");
  }
  return {
    ephemeralPubKey,
    ciphertext: canonicalHexBytes(
      value.ciphertext,
      "Encrypted note ciphertext",
    ),
    iv: canonicalHexBytes(value.iv, "Encrypted note IV", 12),
    authTag: canonicalHexBytes(value.authTag, "Encrypted note auth tag", 16),
  };
}

function encryptedNoteEnvelopesEqual(
  left: FlatJoinSplitV2EncryptedNoteEnvelope,
  right: FlatJoinSplitV2EncryptedNoteEnvelope,
): boolean {
  return (
    left.ephemeralPubKey === right.ephemeralPubKey &&
    left.ciphertext === right.ciphertext &&
    left.iv === right.iv &&
    left.authTag === right.authTag
  );
}

function canonicalScope(scope: FlatJoinSplitV2JournalScope): CanonicalScope {
  const chainId = Number(scope.chainId);
  if (!Number.isSafeInteger(chainId) || chainId <= 0) {
    throw new Error("Settlement journal chain ID is invalid");
  }
  const poolAddress = ethers.getAddress(scope.poolAddress);
  const accountAddress = ethers.getAddress(scope.accountAddress);
  if (
    !(scope.spendingKey instanceof Uint8Array) ||
    scope.spendingKey.length !== 32
  ) {
    throw new Error("Settlement journal key material is invalid");
  }
  return {
    chainId,
    poolAddress,
    accountAddress,
    spendingKey: scope.spendingKey,
    storageKey: `stealthos_flat_joinsplit_v2_journal_v1:${chainId}:${poolAddress.toLowerCase()}:${accountAddress.toLowerCase()}`,
  };
}

function requireBrowserStorage(): void {
  if (
    typeof window === "undefined" ||
    !window.localStorage ||
    !window.crypto?.subtle
  ) {
    throw new Error("Encrypted settlement journal storage is unavailable");
  }
}

async function deriveKey(scope: CanonicalScope): Promise<CryptoKey> {
  requireBrowserStorage();
  const material = new TextEncoder().encode(
    [
      "stealthos-flat-joinsplit-v2-settlement-journal-v1",
      scope.chainId,
      scope.poolAddress.toLowerCase(),
      scope.accountAddress.toLowerCase(),
      ethers.hexlify(scope.spendingKey).slice(2),
    ].join(":"),
  );
  const digest = await window.crypto.subtle.digest("SHA-256", material);
  return window.crypto.subtle.importKey(
    "raw",
    digest,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
}

function recordStoragePrefix(scope: CanonicalScope): string {
  return `${scope.storageKey}:record:`;
}

function recordStorageKey(scope: CanonicalScope, id: string): string {
  return `${recordStoragePrefix(scope)}${encodeURIComponent(id)}`;
}

async function decryptStoredValue(
  scope: CanonicalScope,
  storageKey: string,
  raw: string,
): Promise<unknown> {
  let envelope: EncryptedJournalEnvelope;
  try {
    envelope = JSON.parse(raw) as EncryptedJournalEnvelope;
  } catch {
    throw new Error("Encrypted settlement journal envelope is malformed");
  }
  if (
    envelope.schemaVersion !== JOURNAL_SCHEMA_VERSION ||
    typeof envelope.iv !== "string" ||
    typeof envelope.ciphertext !== "string"
  ) {
    throw new Error("Encrypted settlement journal envelope is invalid");
  }
  try {
    const key = await deriveKey(scope);
    const plaintext = await window.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: decodeBase64(envelope.iv),
        additionalData: new TextEncoder().encode(storageKey),
      },
      key,
      decodeBase64(envelope.ciphertext),
    );
    return JSON.parse(new TextDecoder().decode(plaintext), reviver);
  } catch (error) {
    throw new Error(
      `Encrypted settlement journal could not be decrypted: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }
}

async function readLegacyRecords(
  scope: CanonicalScope,
): Promise<FlatJoinSplitV2JournalRecord[]> {
  requireBrowserStorage();
  const raw = window.localStorage.getItem(scope.storageKey);
  if (!raw) return [];
  const parsed = await decryptStoredValue(scope, scope.storageKey, raw);
  if (!Array.isArray(parsed)) {
    throw new Error("Encrypted settlement journal legacy body is not an array");
  }
  return parsed as FlatJoinSplitV2JournalRecord[];
}

async function readIndependentPayloads(
  scope: CanonicalScope,
): Promise<Map<string, JournalRecordPayload>> {
  requireBrowserStorage();
  const prefix = recordStoragePrefix(scope);
  const keys: string[] = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (key?.startsWith(prefix)) keys.push(key);
  }
  const payloads = new Map<string, JournalRecordPayload>();
  for (const key of keys) {
    const raw = window.localStorage.getItem(key);
    if (!raw) continue;
    const parsed = (await decryptStoredValue(
      scope,
      key,
      raw,
    )) as JournalRecordPayload;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      (parsed.type !== "record" && parsed.type !== "deleted")
    ) {
      throw new Error("Encrypted settlement journal record body is invalid");
    }
    const id = parsed.type === "record" ? parsed.record?.id : parsed.id;
    if (
      typeof id !== "string" ||
      id.length === 0 ||
      recordStorageKey(scope, id) !== key
    ) {
      throw new Error("Encrypted settlement journal record key is invalid");
    }
    payloads.set(id, parsed);
  }
  return payloads;
}

/** Authenticate one content-addressed record, including deletion markers.
 * Looking up one backup must not decrypt the entire account's journal. */
async function hasIndependentPayload(
  scope: CanonicalScope,
  id: string,
): Promise<boolean> {
  requireBrowserStorage();
  const key = recordStorageKey(scope, id);
  const raw = window.localStorage.getItem(key);
  if (!raw) return false;
  const payload = (await decryptStoredValue(
    scope,
    key,
    raw,
  )) as JournalRecordPayload;
  if (
    !payload ||
    (payload.type !== "record" && payload.type !== "deleted") ||
    (payload.type === "record" ? payload.record?.id : payload.id) !== id
  ) {
    throw new Error("Encrypted settlement journal record key is invalid");
  }
  return true;
}

async function readRecords(
  scope: CanonicalScope,
): Promise<FlatJoinSplitV2JournalRecord[]> {
  const [legacy, independent] = await Promise.all([
    readLegacyRecords(scope),
    readIndependentPayloads(scope),
  ]);
  const records = new Map<string, FlatJoinSplitV2JournalRecord>();
  for (const record of legacy) records.set(record.id, record);
  // An independent entry is authoritative over its legacy-array predecessor.
  for (const [id, payload] of independent) {
    if (payload.type === "deleted") records.delete(id);
    else records.set(id, payload.record);
  }
  return [...records.values()].sort(
    (left, right) =>
      left.createdAt - right.createdAt || left.id.localeCompare(right.id),
  );
}

let persistentStorageRequest: Promise<void> | undefined;

async function requestPersistentStorageBestEffort(): Promise<void> {
  if (!persistentStorageRequest) {
    persistentStorageRequest = (async () => {
      try {
        const storage = globalThis.navigator?.storage;
        if (storage && typeof storage.persist === "function") {
          await storage.persist();
        }
      } catch {
        // Persistence is an availability hint, not an integrity boundary. A
        // denied/unsupported request must not bypass the verified write below.
      }
    })();
  }
  await persistentStorageRequest;
}

async function writePayload(
  scope: CanonicalScope,
  id: string,
  payload: JournalRecordPayload,
  isCurrent?: () => boolean,
): Promise<void> {
  requireBrowserStorage();
  await requestPersistentStorageBestEffort();
  const storageKey = recordStorageKey(scope, id);
  const key = await deriveKey(scope);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(payload, replacer));
  const ciphertext = await window.crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
      additionalData: new TextEncoder().encode(storageKey),
    },
    key,
    plaintext,
  );
  const envelope: EncryptedJournalEnvelope = {
    schemaVersion: JOURNAL_SCHEMA_VERSION,
    iv: encodeBase64(iv),
    ciphertext: encodeBase64(new Uint8Array(ciphertext)),
  };
  const encoded = JSON.stringify(envelope);
  if (isCurrent && !isCurrent())
    throw new Error("Wallet changed during recovery import");
  // Each operation owns one atomic key, so concurrent records cannot clobber
  // one another even when Web Locks are unavailable.
  window.localStorage.setItem(storageKey, encoded);
  if (window.localStorage.getItem(storageKey) !== encoded) {
    throw new Error("Encrypted settlement journal write was not durable");
  }
}

const mutationTails = new Map<string, Promise<void>>();

async function withMutationLock<T>(
  scope: CanonicalScope,
  callback: () => Promise<T>,
): Promise<T> {
  const lockName = `stealthos:journal:${scope.storageKey}`;
  if (globalThis.navigator?.locks) {
    return navigator.locks.request(lockName, { mode: "exclusive" }, callback);
  }
  const previous = mutationTails.get(lockName) ?? Promise.resolve();
  let release!: () => void;
  const gate = new Promise<void>((resolve) => {
    release = resolve;
  });
  const tail = previous.then(() => gate);
  mutationTails.set(lockName, tail);
  await previous;
  try {
    return await callback();
  } finally {
    release();
    if (mutationTails.get(lockName) === tail) mutationTails.delete(lockName);
  }
}

function canonicalIntent(
  value: FlatJoinSplitV2TransactionIntent,
  scope: CanonicalScope,
): FlatJoinSplitV2TransactionIntent {
  const chainId = Number(value.chainId);
  if (chainId !== scope.chainId) {
    throw new Error("Settlement intent chain does not match the journal scope");
  }
  const to = ethers.getAddress(value.to);
  const from = ethers.getAddress(value.from);
  if (
    !isPrivateSettlementTarget(to, scope.poolAddress, value.data) ||
    from !== scope.accountAddress
  ) {
    throw new Error(
      "Settlement intent account or pool does not match its scope",
    );
  }
  if (!ethers.isHexString(value.data) || value.data.length < 10) {
    throw new Error("Settlement intent calldata is malformed");
  }
  let amount: bigint;
  try {
    amount = BigInt(value.value);
  } catch {
    throw new Error("Settlement intent value is malformed");
  }
  if (amount < 0n)
    throw new Error("Settlement intent value cannot be negative");
  return {
    to,
    from,
    data: value.data.toLowerCase(),
    value: amount.toString(),
    chainId,
  };
}

function uniqueFields(values: readonly string[], label: string): string[] {
  const canonical = values.map((value, index) =>
    canonicalField(value, `${label}[${index}]`),
  );
  if (new Set(canonical).size !== canonical.length) {
    throw new Error(`${label} contains duplicates`);
  }
  return canonical;
}

function canonicalOutputNotes(
  notes: readonly JoinSplitNote[],
  scope: CanonicalScope,
  swapPool?: string,
): JoinSplitNote[] {
  if (notes.length > 5)
    throw new Error("Settlement journal has too many outputs");
  const canonical = notes.map((note) => {
    if (
      note.protocol !== "flat-joinsplit-v2" ||
      note.schemaVersion !== 2 ||
      note.chainId !== scope.chainId ||
      !note.poolAddress ||
      (ethers.getAddress(note.poolAddress) !== scope.poolAddress &&
        ethers.getAddress(note.poolAddress) !== swapPool)
    ) {
      throw new Error("Settlement output is outside the journal namespace");
    }
    const commitment = canonicalField(note.commitment, "output commitment");
    return { ...note, commitment };
  });
  if (
    new Set(canonical.map((note) => note.commitment.toLowerCase())).size !==
    canonical.length
  ) {
    throw new Error("Settlement journal output commitments contain duplicates");
  }
  return canonical;
}

async function validateOutputDeliveries(
  outputNotes: readonly JoinSplitNote[],
  deliveries: readonly FlatJoinSplitV2JournalDelivery[],
  scope: CanonicalScope,
): Promise<void> {
  const deliveriesByCommitment = new Map(
    deliveries.map((delivery) => [delivery.commitment, delivery]),
  );
  await Promise.all(
    outputNotes.map(async (note) => {
      const delivery = deliveriesByCommitment.get(
        note.commitment.toLowerCase(),
      );
      if (!delivery) {
        throw new Error("Every settlement output must have delivery metadata");
      }
      if (!note.tokenData?.tokenAddress) {
        throw new Error("Settlement output is missing its token identity");
      }
      await assertFlatJoinSplitV2Note({
        note,
        chainId: scope.chainId,
        poolAddress: note.poolAddress!,
        tokenAddress: note.tokenData.tokenAddress,
        expectedMpk: BigInt(delivery.recipientMpkHex),
      });
    }),
  );
}

function mergeFinalizedOutputNotes(
  original: readonly JoinSplitNote[],
  proposed: readonly JoinSplitNote[],
  scope: CanonicalScope,
): JoinSplitNote[] {
  if (original.length !== proposed.length) {
    throw new Error("Finalized output notes cannot change the output set");
  }

  return original.map((originalNote, index) => {
    const candidate = proposed[index];
    const sameTokenData =
      originalNote.tokenData?.tokenType === candidate.tokenData?.tokenType &&
      BigInt(originalNote.tokenData?.tokenSubID ?? "0") ===
        BigInt(candidate.tokenData?.tokenSubID ?? "0") &&
      (originalNote.tokenData?.tokenAddress === undefined
        ? candidate.tokenData?.tokenAddress === undefined
        : candidate.tokenData?.tokenAddress !== undefined &&
          ethers.getAddress(originalNote.tokenData.tokenAddress) ===
            ethers.getAddress(candidate.tokenData.tokenAddress));
    const sameImmutableFields =
      originalNote.schemaVersion === candidate.schemaVersion &&
      originalNote.protocol === candidate.protocol &&
      originalNote.chainId === candidate.chainId &&
      originalNote.poolAddress !== undefined &&
      candidate.poolAddress !== undefined &&
      ethers.getAddress(originalNote.poolAddress) ===
        ethers.getAddress(candidate.poolAddress) &&
      originalNote.npk === candidate.npk &&
      originalNote.random === candidate.random &&
      originalNote.commitment.toLowerCase() ===
        candidate.commitment.toLowerCase() &&
      originalNote.tokenHash === candidate.tokenHash &&
      originalNote.value === candidate.value &&
      originalNote.treeIndex === candidate.treeIndex &&
      originalNote.treeKey === candidate.treeKey &&
      originalNote.treePda === candidate.treePda &&
      originalNote.nullifier === candidate.nullifier &&
      sameTokenData;
    if (!sameImmutableFields) {
      throw new Error(
        "Finalization cannot mutate settlement output note secrets or identity",
      );
    }
    if (
      !Number.isSafeInteger(candidate.leafIndex) ||
      candidate.leafIndex < 0 ||
      candidate.leafIndex >= 1 << 24
    ) {
      throw new Error("Finalized output note leaf index is invalid");
    }
    const merkleRoot =
      candidate.merkleRoot === undefined
        ? originalNote.merkleRoot
        : canonicalField(candidate.merkleRoot, "output Merkle root");
    return {
      ...originalNote,
      chainId: scope.chainId,
      poolAddress: candidate.poolAddress,
      leafIndex: candidate.leafIndex,
      merkleRoot,
    };
  });
}

function randomId(): string {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  return ethers.hexlify(window.crypto.getRandomValues(new Uint8Array(16)));
}

function isPrivateBridgeJournal(record: {
  kind: FlatJoinSplitV2JournalKind;
  transaction: FlatJoinSplitV2TransactionIntent;
}): boolean {
  if (record.kind !== "withdraw") return false;
  const decoded = decodePrivateBridge(record.transaction.data);
  return (
    !!decoded &&
    record.transaction.to.toLowerCase() ===
      bridgeDeploymentFor(decoded.provider)?.coordinator.toLowerCase()
  );
}

function bridgeCompletionId(data: string): string {
  return `bridge-completed:${ethers.keccak256(data)}`;
}

/** Ignore bookkeeping only. Different recovery material or progress is never
 * interchangeable, even when two records carry the same transaction. */
function recoverySnapshot(record: FlatJoinSplitV2JournalRecord): string {
  return JSON.stringify(
    { ...record, id: "", createdAt: 0, updatedAt: 0 },
    replacer,
  );
}

/** Older bridge restores allocated a random ID on every poll. Retain one
 * durable copy of each identical PREPARED snapshot, then tombstone only its
 * exact duplicates. Never evict distinct or progressed records for capacity. */
export async function consolidatePrivateBridgeJournals(
  scopeInput: FlatJoinSplitV2JournalScope,
): Promise<number> {
  const scope = canonicalScope(scopeInput);
  return withMutationLock(scope, async () => {
    const records = await readRecords(scope);
    const retained = new Map<string, FlatJoinSplitV2JournalRecord>();
    let consolidated = 0;
    for (const record of records) {
      if (record.state !== "prepared" || !isPrivateBridgeJournal(record))
        continue;
      canonicalIntent(record.transaction, scope);
      const snapshot = recoverySnapshot(record);
      const original = retained.get(snapshot);
      if (!original) {
        retained.set(snapshot, record);
        continue;
      }
      // Verify an independent retained copy before suppressing any duplicate,
      // including when the original came from the old encrypted array format.
      await writePayload(scope, original.id, {
        type: "record",
        record: original,
      });
      await writePayload(scope, record.id, { type: "deleted", id: record.id });
      consolidated++;
    }
    return consolidated;
  });
}

export async function prepareFlatJoinSplitV2SettlementJournal(
  scopeInput: FlatJoinSplitV2JournalScope,
  input: {
    kind: FlatJoinSplitV2JournalKind;
    proofDisclosure?: "undisclosed";
    transaction: FlatJoinSplitV2TransactionIntent;
    sourceCommitments?: string[];
    sourceNullifiers?: string[];
    outputNotes?: JoinSplitNote[];
    deliveries?: FlatJoinSplitV2JournalDelivery[];
    actionHash?: string;
    swapOutputSeed?: FlatJoinSplitV2SwapOutputSeed;
  },
): Promise<FlatJoinSplitV2JournalRecord> {
  const scope = canonicalScope(scopeInput);
  const transaction = canonicalIntent(input.transaction, scope);
  const sourceCommitments = uniqueFields(
    input.sourceCommitments ?? [],
    "source commitments",
  );
  const sourceNullifiers = uniqueFields(
    input.sourceNullifiers ?? [],
    "source nullifiers",
  );
  if (sourceCommitments.length > 32 || sourceNullifiers.length > 32) {
    throw new Error("Settlement journal has too many inputs");
  }
  const outputNotes = canonicalOutputNotes(input.outputNotes ?? [], scope);
  const outputCommitments = new Set(
    outputNotes.map((note) => note.commitment.toLowerCase()),
  );
  const deliveries = (input.deliveries ?? []).map((delivery) => {
    if (
      delivery.encryptedNote !== undefined ||
      delivery.durableBackup !== undefined ||
      delivery.disposition !== undefined ||
      delivery.completedAt !== undefined
    ) {
      throw new Error(
        "Settlement delivery state cannot be supplied before finality",
      );
    }
    const commitment = canonicalField(
      delivery.commitment,
      "delivery commitment",
    );
    if (!outputCommitments.has(commitment)) {
      throw new Error(
        "Delivery metadata does not reference a settlement output",
      );
    }
    const recipientMpkHex = canonicalField(
      delivery.recipientMpkHex,
      "delivery recipient MPK",
    );
    if (
      delivery.recipientInboxPublicKeyHex !== undefined &&
      !ethers.isHexString(delivery.recipientInboxPublicKeyHex, 32)
    ) {
      throw new Error("Delivery inbox public key must be exactly 32 bytes");
    }
    if (!delivery.keepLocal && !delivery.recipientInboxPublicKeyHex) {
      throw new Error(
        "External settlement output is missing inbox routing data",
      );
    }
    return {
      commitment,
      recipientMpkHex,
      recipientInboxPublicKeyHex:
        delivery.recipientInboxPublicKeyHex?.toLowerCase(),
      keepLocal: Boolean(delivery.keepLocal),
    };
  });
  if (deliveries.length !== outputNotes.length) {
    throw new Error("Every settlement output must have delivery metadata");
  }
  if (
    new Set(deliveries.map((delivery) => delivery.commitment)).size !==
    deliveries.length
  ) {
    throw new Error("Settlement delivery metadata contains duplicates");
  }
  await validateOutputDeliveries(outputNotes, deliveries, scope);
  let swapOutputSeed: FlatJoinSplitV2SwapOutputSeed | undefined;
  if (input.kind === "swap") {
    if (!input.swapOutputSeed)
      throw new Error(
        "Swap settlement requires durable output recovery material",
      );
    swapOutputSeed = { ...input.swapOutputSeed };
    const { spend, current } = await validateSwapOutputSeed(
      swapOutputSeed,
      transaction.data,
    );
    if (
      Array.from(spend.nullifiers, String)
        .map((value) => value.toLowerCase())
        .join(":") !== sourceNullifiers.join(":")
    ) {
      throw new Error("Swap recovery nullifiers do not match the transaction");
    }
    const change = current
      ? outputNotes[0]?.commitment || ethers.ZeroHash
      : String(spend.changeCommitment).toLowerCase();
    if (
      current &&
      (outputNotes.length > 1 ||
        (outputNotes.length === 1 && spend.commitments[0] !== change))
    )
      throw Error("Swap change output mismatch");
    if (
      (change === ethers.ZeroHash && outputNotes.length !== 0) ||
      (change !== ethers.ZeroHash &&
        (outputNotes.length !== 1 || outputNotes[0].commitment !== change))
    ) {
      throw new Error("Swap recovery change does not match the transaction");
    }
  } else if (input.swapOutputSeed) {
    throw new Error(
      "Only swap settlements may contain output recovery material",
    );
  }
  const timestamp = Date.now();
  let record: FlatJoinSplitV2JournalRecord = {
    schemaVersion: JOURNAL_SCHEMA_VERSION,
    id:
      (input.kind === "swap" && decodePrivateSwap(transaction.data)) ||
      isPrivateBridgeJournal({ kind: input.kind, transaction })
        ? ethers.keccak256(transaction.data)
        : randomId(),
    state: "prepared",
    ...(input.proofDisclosure === "undisclosed"
      ? { proofDisclosure: "undisclosed" as const }
      : {}),
    kind: input.kind,
    chainId: scope.chainId,
    poolAddress: scope.poolAddress,
    accountAddress: scope.accountAddress,
    transaction,
    calldataHash: ethers.keccak256(transaction.data),
    actionHash: input.actionHash
      ? canonicalField(input.actionHash, "action hash")
      : undefined,
    sourceCommitments,
    sourceNullifiers,
    outputNotes,
    deliveries,
    swapOutputSeed,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
  await withMutationLock(scope, async () => {
    const records = await readRecords(scope);
    const existing = records.find((r) => r.id === record.id);
    if (
      isPrivateBridgeJournal(record) &&
      ((await hasIndependentPayload(
        scope,
        bridgeCompletionId(transaction.data),
      )) ||
        (!existing && (await hasIndependentPayload(scope, record.id))))
    )
      throw new Error("Bridge settlement was already reconciled");
    if (
      existing &&
      (input.kind === "swap" ||
        (isPrivateBridgeJournal(record) &&
          recoverySnapshot(existing) === recoverySnapshot(record))) &&
      existing.transaction.data === transaction.data &&
      existing.state === "prepared"
    ) {
      record = existing;
      return;
    }
    if (existing) {
      throw new Error("Settlement journal record ID collision");
    }
    // Explicit wallet rejections are inactive, but their encrypted recovery
    // material is retained: simulation already exposed a copyable pool proof.
    // Archives remain discoverable by reconciliation and count toward browser
    // storage quota. Never evict them to make a new write succeed.
    const reservedInputs = new Set(sourceCommitments);
    const reservedNullifiers = new Set(sourceNullifiers);
    const overlapping = records.filter(
      (candidate) =>
        (candidate.state === "prepared" ||
          candidate.state === "broadcast" ||
          candidate.state === "included") &&
        (candidate.sourceCommitments.some((commitment) =>
          reservedInputs.has(commitment),
        ) ||
          candidate.sourceNullifiers.some((nullifier) =>
            reservedNullifiers.has(nullifier),
          )),
    );
    // Supersession and disclosure use the same cross-tab lock. If disclosure
    // wins, the input stays reserved. If replacement wins, the old attempt can
    // never pass markDisclosed, even if its backup acknowledgement arrives late.
    // Without Web Locks, retain the record for explicit same-intent retry.
    if (
      overlapping.some(
        (candidate) =>
          !globalThis.navigator?.locks ||
          candidate.state !== "prepared" ||
          candidate.proofDisclosure !== "undisclosed",
      )
    ) {
      throw new Error(
        "An input is reserved by an unsettled transaction; reconcile it before retrying",
      );
    }
    const activeCount = records.filter(
      (candidate) =>
        candidate.state !== "wallet_rejected" &&
        candidate.state !== "preparation_failed" &&
        candidate.state !== "input_superseded" &&
        !overlapping.includes(candidate),
    ).length;
    if (activeCount >= FLAT_JOINSPLIT_V2_MAX_ACTIVE_JOURNAL_RECORDS) {
      throw new Error(
        "Settlement journal is full; reconcile old records first",
      );
    }
    for (const candidate of overlapping)
      await writePayload(scope, candidate.id, {
        type: "record",
        record: {
          ...candidate,
          state: "preparation_failed",
          updatedAt: Date.now(),
        },
      });
    await writePayload(scope, record.id, { type: "record", record });
  });
  return record;
}

async function updateRecord(
  scopeInput: FlatJoinSplitV2JournalScope,
  id: string,
  updater: (
    record: FlatJoinSplitV2JournalRecord,
  ) => FlatJoinSplitV2JournalRecord | Promise<FlatJoinSplitV2JournalRecord>,
): Promise<FlatJoinSplitV2JournalRecord> {
  const scope = canonicalScope(scopeInput);
  return withMutationLock(scope, async () => {
    const records = await readRecords(scope);
    const record = records.find((candidate) => candidate.id === id);
    if (!record) throw new Error("Settlement journal record was not found");
    const updated = await updater(record);
    if (STATE_RANK[updated.state] < STATE_RANK[record.state]) {
      throw new Error("Settlement journal state cannot move backwards");
    }
    const durable = { ...updated, updatedAt: Date.now() };
    await writePayload(scope, id, { type: "record", record: durable });
    return durable;
  });
}

export function listFlatJoinSplitV2SettlementJournal(
  scopeInput: FlatJoinSplitV2JournalScope,
): Promise<FlatJoinSplitV2JournalRecord[]> {
  return readRecords(canonicalScope(scopeInput));
}

/** Persist BEFORE any plaintext proof reaches an RPC or authorization service. */
export function markFlatJoinSplitV2SettlementDisclosed(
  scope: FlatJoinSplitV2JournalScope,
  id: string,
): Promise<FlatJoinSplitV2JournalRecord> {
  return updateRecord(scope, id, (record) => {
    if (record.state !== "prepared")
      throw new Error(
        "Settlement preparation was superseded or already submitted",
      );
    return { ...record, proofDisclosure: "possible" };
  });
}

/** Retain recovery material, but release an attempt that never shared a proof. */
export async function archiveFlatJoinSplitV2UndisclosedPreparation(
  scopeInput: FlatJoinSplitV2JournalScope,
  id: string,
): Promise<void> {
  const scope = canonicalScope(scopeInput);
  await withMutationLock(scope, async () => {
    const record = (await readRecords(scope)).find(
      (candidate) => candidate.id === id,
    );
    if (!record) throw new Error("Settlement journal record was not found");
    if (record.state !== "prepared" || record.proofDisclosure !== "undisclosed")
      return;
    await writePayload(scope, id, {
      type: "record",
      record: { ...record, state: "preparation_failed", updatedAt: Date.now() },
    });
  });
}

/** The caller must prove a successful different intent consumed a bound input
 * at a hash-pinned finalized block, and prove none of this intent's outputs exist.
 * Keep all secrets; only the now impossible input reservation becomes inactive. */
export function markFlatJoinSplitV2SettlementInputSuperseded(
  scope: FlatJoinSplitV2JournalScope,
  id: string,
  expectedCalldataHash: string,
  conflict: NonNullable<FlatJoinSplitV2JournalRecord["inputConflict"]>,
): Promise<FlatJoinSplitV2JournalRecord> {
  return updateRecord(scope, id, (record) => {
    if (
      !(
        record.state === "prepared" ||
        record.state === "broadcast" ||
        record.state === "included"
      ) ||
      record.calldataHash !== expectedCalldataHash ||
      !record.sourceNullifiers.includes(
        canonicalField(conflict.nullifier, "conflicting nullifier"),
      ) ||
      !Number.isSafeInteger(conflict.blockNumber) ||
      conflict.blockNumber < 0
    )
      throw new Error(
        "Finalized input conflict does not match the active settlement",
      );
    return {
      ...record,
      state: "input_superseded",
      inputConflict: {
        nullifier: canonicalField(conflict.nullifier, "conflicting nullifier"),
        txHash: canonicalTxHash(conflict.txHash, "conflicting transaction"),
        blockNumber: conflict.blockNumber,
        blockHash: canonicalTxHash(conflict.blockHash, "conflicting block"),
      },
    };
  });
}

/** Replace only the missing authorization, never the backed-up merge or fees.
 * The immutable remote template remains sufficient to authenticate chain outcome.
 */
export async function authorizeFlatDepositMergeJournal(
  scope: FlatJoinSplitV2JournalScope,
  id: string,
  data: string,
): Promise<FlatJoinSplitV2JournalRecord> {
  return updateRecord(scope, id, (record) => {
    const protectedBefore = unwrapRecoveryPoolCall(record.transaction.data);
    const before = PRIVATE_DEPOSIT_MERGE_INTERFACE.parseTransaction({
      data: recoveryPoolInnerCall(record.transaction.data),
    });
    const after = PRIVATE_DEPOSIT_MERGE_INTERFACE.parseTransaction({
      data: recoveryPoolInnerCall(data),
    });
    if (
      record.kind !== "deposit" ||
      record.state !== "prepared" ||
      !before ||
      !after ||
      before.name !== after.name ||
      before.args.signature !== "0x" ||
      after.args.signature === "0x" ||
      after.args.quote.termsHash !== before.args.quote.termsHash ||
      after.args.quote.nonce !== before.args.quote.nonce ||
      after.args.quote.deadline !== before.args.quote.deadline ||
      after.args.quote.kind !== 5n
    ) {
      throw new Error(
        "Deposit merge authorization changed its recovery intent",
      );
    }
    const expectedArgs = Array.from(before.args);
    expectedArgs[expectedArgs.length - 2] = after.args.quote;
    expectedArgs[expectedArgs.length - 1] = after.args.signature;
    const expectedInner = PRIVATE_DEPOSIT_MERGE_INTERFACE.encodeFunctionData(
      before.fragment,
      expectedArgs,
    );
    const expectedData = protectedBefore
      ? wrapRecoveryPoolCall(expectedInner, protectedBefore.records)
      : expectedInner;
    if (expectedData.toLowerCase() !== data.toLowerCase()) {
      throw new Error(
        "Deposit merge authorization changed its backed-up funding or outputs",
      );
    }
    return {
      ...record,
      transaction: { ...record.transaction, data },
      calldataHash: ethers.keccak256(data),
    };
  });
}

export async function reservedFlatJoinSplitV2Commitments(
  scope: FlatJoinSplitV2JournalScope,
): Promise<Set<string>> {
  const records = await listFlatJoinSplitV2SettlementJournal(scope);
  return new Set(
    records
      .filter(
        (record) =>
          (record.state === "prepared" &&
            (!globalThis.navigator?.locks ||
              record.proofDisclosure !== "undisclosed")) ||
          record.state === "broadcast" ||
          record.state === "included",
      )
      .flatMap((record) =>
        record.sourceCommitments.map((commitment) => commitment.toLowerCase()),
      ),
  );
}

/** Called only after canonical finalized history proves no output and an unused input. */
export async function retireExpiredFlatDepositMergeJournalRecord(
  scopeInput: FlatJoinSplitV2JournalScope,
  id: string,
  finalizedTimestamp: number,
): Promise<void> {
  const scope = canonicalScope(scopeInput);
  await withMutationLock(scope, async () => {
    const record = (await readRecords(scope)).find(
      (candidate) => candidate.id === id,
    );
    if (!record) return;
    const merge = depositMergeEvidenceFromJournal(record);
    if (
      !merge ||
      !Number.isSafeInteger(finalizedTimestamp) ||
      BigInt(finalizedTimestamp) <= merge.deadline ||
      record.state === "finalized" ||
      record.state === "delivery_pending"
    ) {
      throw new Error("Deposit merge expiry is not established");
    }
    depositNoteFromJournal(record);
    await writePayload(scope, id, { type: "deleted", id });
  });
}
export async function hasPrivateSwapJournal(
  scope: FlatJoinSplitV2JournalScope,
  data: string,
) {
  return hasIndependentPayload(canonicalScope(scope), ethers.keccak256(data));
}

export async function hasPrivateBridgeJournal(
  scopeInput: FlatJoinSplitV2JournalScope,
  data: string,
): Promise<boolean> {
  const scope = canonicalScope(scopeInput);
  if (
    (await hasIndependentPayload(scope, ethers.keccak256(data))) ||
    (await hasIndependentPayload(scope, bridgeCompletionId(data)))
  )
    return true;
  // Recognize the random IDs written by older clients without recreating the
  // transaction or resetting its recorded finality/delivery progress.
  return (await readRecords(scope)).some(
    (record) =>
      isPrivateBridgeJournal(record) &&
      record.transaction.data.toLowerCase() === data.toLowerCase(),
  );
}

export async function exportFlatJournalBackup(
  scopeInput: FlatJoinSplitV2JournalScope,
  id: string,
): Promise<FlatJournalBackup> {
  const scope = canonicalScope(scopeInput);
  requireBrowserStorage();
  const storageKey = recordStorageKey(scope, id);
  const raw = window.localStorage.getItem(storageKey);
  if (!raw) throw new Error("Prepared recovery record is missing");
  const payload = (await decryptStoredValue(
    scope,
    storageKey,
    raw,
  )) as JournalRecordPayload;
  if (
    payload.type !== "record" ||
    payload.record.id !== id ||
    payload.record.state !== "prepared"
  ) {
    throw new Error(
      "Only a prepared settlement may be backed up before disclosure",
    );
  }
  const envelope = JSON.parse(raw) as EncryptedJournalEnvelope;
  return {
    id,
    chainId: scope.chainId,
    poolAddress: scope.poolAddress.toLowerCase(),
    accountAddress: scope.accountAddress.toLowerCase(),
    envelope: {
      schemaVersion: envelope.schemaVersion,
      iv: envelope.iv,
      ciphertext: envelope.ciphertext,
    },
  };
}

/** Add authenticated missing snapshots; never overwrite newer local state or tombstones.
 * Restored records are still PREPARED, not spendable/finalized: chain reconciliation
 * must establish inclusion, ownership and nullifiers. A server cannot create keys.
 */
async function validatedFlatJournalBackupRecord(
  scopeInput: FlatJoinSplitV2JournalScope,
  backup: FlatJournalBackup,
): Promise<FlatJoinSplitV2JournalRecord> {
  const scope = canonicalScope(scopeInput);
  if (
    !backup ||
    !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(backup.id) ||
    backup.chainId !== scope.chainId ||
    ethers.getAddress(backup.poolAddress) !== scope.poolAddress ||
    ethers.getAddress(backup.accountAddress) !== scope.accountAddress
  ) {
    throw new Error("Recovery backup namespace mismatch");
  }
  const raw = JSON.stringify(backup.envelope);
  if (raw.length > 360 * 1024)
    throw new Error("Recovery backup exceeds size limit");
  const storageKey = recordStorageKey(scope, backup.id);
  const payload = (await decryptStoredValue(
    scope,
    storageKey,
    raw,
  )) as JournalRecordPayload;
  const record = payload?.type === "record" ? payload.record : undefined;
  if (
    !record ||
    record.schemaVersion !== JOURNAL_SCHEMA_VERSION ||
    record.id !== backup.id ||
    record.state !== "prepared" ||
    (record.proofDisclosure !== undefined &&
      record.proofDisclosure !== "undisclosed" &&
      record.proofDisclosure !== "possible") ||
    record.chainId !== scope.chainId ||
    ethers.getAddress(record.poolAddress) !== scope.poolAddress ||
    ethers.getAddress(record.accountAddress) !== scope.accountAddress ||
    record.transaction.chainId !== scope.chainId ||
    !isPrivateSettlementTarget(
      record.transaction.to,
      scope.poolAddress,
      record.transaction.data,
    ) ||
    ethers.getAddress(record.transaction.from) !== scope.accountAddress ||
    record.calldataHash !== ethers.keccak256(record.transaction.data) ||
    !Array.isArray(record.outputNotes) ||
    !Array.isArray(record.deliveries) ||
    record.outputNotes.length !== record.deliveries.length ||
    !Array.isArray(record.sourceCommitments) ||
    !Array.isArray(record.sourceNullifiers) ||
    !(
      record.kind === "deposit" ||
      record.kind === "joinsplit" ||
      record.kind === "withdraw" ||
      record.kind === "withdraw_batch" ||
      record.kind === "migrate" ||
      record.kind === "swap" ||
      record.kind === "bridge"
    )
  ) {
    throw new Error("Recovery backup is not a valid prepared journal record");
  }
  if (record.swapOutputSeed)
    await validateSwapOutputSeed(
      record.swapOutputSeed,
      record.transaction.data,
    );
  const outputs = canonicalOutputNotes(
    record.outputNotes,
    scope,
    record.swapOutputSeed?.outputPoolAddress,
  );
  await validateOutputDeliveries(outputs, record.deliveries, scope);
  return record;
}

export async function validateFlatJournalBackup(
  scope: FlatJoinSplitV2JournalScope,
  backup: FlatJournalBackup,
): Promise<void> {
  await validatedFlatJournalBackupRecord(scope, backup);
}

/** The original ciphertext is immutable. A local disclosure update must not
 * replace it; authenticate its complete recovery intent before reusing its ACK. */
export async function assertFlatJournalBackupMatchesRecord(
  scope: FlatJoinSplitV2JournalScope,
  backup: FlatJournalBackup,
  id: string,
): Promise<void> {
  const restored = await validatedFlatJournalBackupRecord(scope, backup);
  const current = (await listFlatJoinSplitV2SettlementJournal(scope)).find(
    (record) => record.id === id,
  );
  const snapshot = (record: FlatJoinSplitV2JournalRecord) =>
    recoverySnapshot({
      ...record,
      proofDisclosure: undefined,
    });
  if (
    !current ||
    current.state !== "prepared" ||
    backup.id !== id ||
    snapshot(restored) !== snapshot(current)
  )
    throw new Error(
      "Durable backup does not match the saved settlement intent",
    );
}

export async function importFlatJournalBackup(
  scopeInput: FlatJoinSplitV2JournalScope,
  backup: FlatJournalBackup,
  isCurrent: () => boolean = () => true,
): Promise<boolean> {
  const record = await validatedFlatJournalBackupRecord(scopeInput, backup);
  const scope = canonicalScope(scopeInput);
  return withMutationLock(scope, async () => {
    // Decrypt/check the existing local record before deciding it supersedes a backup.
    if (
      (await hasIndependentPayload(scope, backup.id)) ||
      (await readLegacyRecords(scope)).some((r) => r.id === backup.id)
    )
      return false;
    // The backup necessarily predates disclosure. Its local-only flag cannot
    // establish what happened later on the original device.
    await writePayload(
      scope,
      backup.id,
      {
        type: "record",
        record: { ...record, proofDisclosure: "possible" },
      },
      isCurrent,
    );
    return true;
  });
}

/**
 * Archive only a freshly prepared, explicitly rejected wallet submission or a
 * local AbortSignal cancellation observed before any submission was attempted.
 * This is not proof that the settlement can never execute: eth_call already
 * revealed its calldata, so outputs/seeds must survive for later reconciliation.
 * Older prepared records and ambiguous submissions cannot be classified here
 * without fresh evidence of a wallet rejection. No automatic migration/purge.
 */
export function markFlatJoinSplitV2SettlementWalletRejected(
  scope: FlatJoinSplitV2JournalScope,
  id: string,
): Promise<FlatJoinSplitV2JournalRecord> {
  return updateRecord(scope, id, (record) => {
    if (
      record.state !== "prepared" ||
      record.txHash !== undefined ||
      record.blockNumber !== undefined ||
      record.blockHash !== undefined ||
      record.swapOutputCommitment !== undefined ||
      record.outputNotes.some((note) => note.leafIndex !== -1) ||
      record.deliveries.some(
        (delivery) =>
          delivery.encryptedNote !== undefined ||
          delivery.durableBackup !== undefined ||
          delivery.disposition !== undefined ||
          delivery.completedAt !== undefined,
      )
    ) {
      throw new Error(
        "Only an unsubmitted prepared settlement can be archived",
      );
    }
    return { ...record, state: "wallet_rejected" };
  });
}

export function markFlatJoinSplitV2SettlementBroadcast(
  scope: FlatJoinSplitV2JournalScope,
  id: string,
  txHash: string,
): Promise<FlatJoinSplitV2JournalRecord> {
  const hash = canonicalTxHash(txHash, "broadcast hash");
  return updateRecord(scope, id, (record) => ({
    ...record,
    state: "broadcast",
    txHash: hash,
  }));
}

export function markFlatJoinSplitV2SettlementIncluded(
  scope: FlatJoinSplitV2JournalScope,
  id: string,
  details: { txHash: string; blockNumber: number; blockHash: string },
): Promise<FlatJoinSplitV2JournalRecord> {
  if (!Number.isSafeInteger(details.blockNumber) || details.blockNumber < 0) {
    throw new Error("Settlement inclusion block is invalid");
  }
  const txHash = canonicalTxHash(details.txHash, "inclusion transaction hash");
  const blockHash = canonicalTxHash(details.blockHash, "inclusion block hash");
  return updateRecord(scope, id, (record) => ({
    ...record,
    state: "included",
    txHash,
    blockNumber: details.blockNumber,
    blockHash,
  }));
}

export function markFlatJoinSplitV2SettlementFinalized(
  scope: FlatJoinSplitV2JournalScope,
  id: string,
  details: {
    txHash: string;
    blockNumber: number;
    blockHash: string;
    outputNotes?: JoinSplitNote[];
    swapOutputNote?: JoinSplitNote;
    swapRecovered?: boolean;
  },
): Promise<FlatJoinSplitV2JournalRecord> {
  if (!Number.isSafeInteger(details.blockNumber) || details.blockNumber < 0) {
    throw new Error("Settlement finalization block is invalid");
  }
  const txHash = canonicalTxHash(details.txHash, "final transaction hash");
  const blockHash = canonicalTxHash(details.blockHash, "final block hash");
  const canonical = canonicalScope(scope);
  return updateRecord(scope, id, async (record) => {
    if (record.swapOutputSeed)
      await validateSwapOutputSeed(
        record.swapOutputSeed,
        record.transaction.data,
      );
    const outputNotes = details.outputNotes
      ? canonicalOutputNotes(
          details.outputNotes,
          canonical,
          record.swapOutputSeed?.outputPoolAddress,
        )
      : undefined;
    const finalizedOutputNotes = outputNotes
      ? mergeFinalizedOutputNotes(record.outputNotes, outputNotes, canonical)
      : record.outputNotes;
    let deliveries = record.deliveries;
    let swapOutputCommitment = record.swapOutputCommitment;
    if (record.swapOutputSeed) {
      if (!details.swapOutputNote)
        throw new Error("Swap output must be recovered before finalization");
      const { swap, current } = await validateSwapOutputSeed(
        record.swapOutputSeed,
        record.transaction.data,
      );
      const destination =
        record.swapOutputSeed.outputPoolAddress || canonical.poolAddress;
      const note = canonicalOutputNotes([details.swapOutputNote], {
        ...canonical,
        poolAddress: destination,
      })[0];
      if (details.swapRecovered && !current)
        throw Error("Unexpected swap recovery");
      const recoveredToken =
        current && (current.recovery || details.swapRecovered)
          ? current.route.inputToken
          : record.swapOutputSeed.outputTokenAddress;
      await assertFlatJoinSplitV2Note({
        note,
        chainId: canonical.chainId,
        poolAddress: destination,
        tokenAddress: recoveredToken,
        expectedMpk: record.swapOutputSeed.recipientMpk,
      });
      if (
        note.random !== record.swapOutputSeed.random ||
        note.npk !== BigInt(record.swapOutputSeed.outputNpk) ||
        note.value <
          BigInt(
            current && (current.recovery || details.swapRecovered)
              ? current.route.amountIn
              : swap.minOutputAmount,
          ) ||
        note.leafIndex < 0 ||
        note.leafIndex >= 1 << 24 ||
        !Number.isSafeInteger(note.leafIndex)
      )
        throw new Error(
          "Recovered swap output does not match the durable seed",
        );
      if (swapOutputCommitment) {
        const original = finalizedOutputNotes.find(
          (output) => output.commitment === swapOutputCommitment,
        );
        if (!original)
          throw new Error("Recovered swap output is missing from the journal");
        mergeFinalizedOutputNotes([original], [note], canonical);
      } else {
        if (
          finalizedOutputNotes.some(
            (output) => output.commitment === note.commitment,
          )
        ) {
          throw new Error(
            "Recovered swap output duplicates a settlement output",
          );
        }
        finalizedOutputNotes.push(note);
        deliveries = [
          ...deliveries,
          {
            commitment: note.commitment,
            recipientMpkHex: ethers
              .toBeHex(record.swapOutputSeed.recipientMpk, 32)
              .toLowerCase(),
            keepLocal: true,
          },
        ];
        swapOutputCommitment = note.commitment;
      }
    } else if (details.swapOutputNote) {
      throw new Error("Cannot append an output without a durable swap seed");
    }
    return {
      ...record,
      state:
        record.state === "delivery_pending" ? "delivery_pending" : "finalized",
      txHash,
      blockNumber: details.blockNumber,
      blockHash,
      outputNotes: finalizedOutputNotes,
      deliveries,
      swapOutputCommitment,
    };
  });
}

export function markFlatJoinSplitV2SettlementDeliveryPending(
  scope: FlatJoinSplitV2JournalScope,
  id: string,
): Promise<FlatJoinSplitV2JournalRecord> {
  return updateRecord(scope, id, (record) => {
    if (record.state !== "finalized" && record.state !== "delivery_pending") {
      throw new Error("Only a finalized settlement can await note delivery");
    }
    return { ...record, state: "delivery_pending" };
  });
}

function deliveryIndexForCommitment(
  record: FlatJoinSplitV2JournalRecord,
  commitment: string,
): number {
  const matchingOutputs = record.outputNotes.filter(
    (note) =>
      canonicalField(note.commitment, "output commitment") === commitment,
  );
  const matchingDeliveries = record.deliveries
    .map((delivery, index) => ({ delivery, index }))
    .filter(
      ({ delivery }) =>
        canonicalField(delivery.commitment, "delivery commitment") ===
        commitment,
    );
  if (matchingOutputs.length !== 1 || matchingDeliveries.length !== 1) {
    throw new Error(
      "Settlement output and delivery metadata are not in one-to-one correspondence",
    );
  }
  return matchingDeliveries[0].index;
}

function requireFinalizedDeliveryRecord(
  record: FlatJoinSplitV2JournalRecord,
): void {
  if (record.state !== "finalized" && record.state !== "delivery_pending") {
    throw new Error("Note delivery can only change after settlement finality");
  }
}

/**
 * Durably stage the exact ciphertext that will be sent for one output. Retrying
 * the same envelope is safe; replacing it would make delivery acknowledgements
 * ambiguous and is therefore rejected.
 */
export function stageFlatJoinSplitV2SettlementDeliveryEnvelope(
  scope: FlatJoinSplitV2JournalScope,
  id: string,
  commitmentInput: string,
  encryptedNoteInput: FlatJoinSplitV2EncryptedNoteEnvelope,
): Promise<FlatJoinSplitV2JournalRecord> {
  const commitment = canonicalField(
    commitmentInput,
    "staged delivery commitment",
  );
  const encryptedNote = canonicalEncryptedNoteEnvelope(encryptedNoteInput);
  return updateRecord(scope, id, (record) => {
    requireFinalizedDeliveryRecord(record);
    const deliveryIndex = deliveryIndexForCommitment(record, commitment);
    const delivery = record.deliveries[deliveryIndex];
    if (
      delivery.completedAt !== undefined &&
      delivery.disposition === undefined
    ) {
      throw new Error("Settlement delivery terminal state is malformed");
    }
    if (delivery.encryptedNote) {
      const staged = canonicalEncryptedNoteEnvelope(delivery.encryptedNote);
      if (!encryptedNoteEnvelopesEqual(staged, encryptedNote)) {
        throw new Error(
          "A different encrypted note envelope is already staged",
        );
      }
      return { ...record, state: "delivery_pending" };
    }
    if (delivery.disposition !== undefined) {
      throw new Error("A completed settlement delivery cannot be restaged");
    }
    const deliveries = [...record.deliveries];
    deliveries[deliveryIndex] = { ...delivery, encryptedNote };
    return { ...record, state: "delivery_pending", deliveries };
  });
}

/** Mark one output terminal after its durable external delivery attempt. */
export function markFlatJoinSplitV2SettlementDeliveryCompleted(
  scope: FlatJoinSplitV2JournalScope,
  id: string,
  commitmentInput: string,
  disposition: FlatJoinSplitV2DeliveryDisposition,
  evidence?: { durableBackup: FlatJoinSplitV2DeliveryBackupEvidence },
): Promise<FlatJoinSplitV2JournalRecord> {
  if (disposition !== "delivered" && disposition !== "superseded") {
    throw new Error("Settlement delivery disposition is invalid");
  }
  const commitment = canonicalField(
    commitmentInput,
    "completed delivery commitment",
  );
  return updateRecord(scope, id, (record) => {
    // Validated inside the update so malformed evidence rejects like every
    // other journal state error instead of throwing before the promise exists.
    const durableBackup =
      evidence?.durableBackup === undefined
        ? undefined
        : canonicalDeliveryBackupEvidence(evidence.durableBackup);
    requireFinalizedDeliveryRecord(record);
    const deliveryIndex = deliveryIndexForCommitment(record, commitment);
    const delivery = record.deliveries[deliveryIndex];
    if (delivery.disposition !== undefined) {
      if (
        delivery.disposition !== disposition ||
        !Number.isSafeInteger(delivery.completedAt) ||
        (delivery.completedAt ?? 0) <= 0
      ) {
        throw new Error(
          "Settlement delivery already has a terminal disposition",
        );
      }
      return { ...record, state: "delivery_pending" };
    }
    if (delivery.completedAt !== undefined) {
      throw new Error("Settlement delivery terminal state is malformed");
    }
    if (
      disposition === "delivered" &&
      !delivery.encryptedNote &&
      !durableBackup
    ) {
      throw new Error(
        "A delivered settlement output requires a staged encrypted note envelope",
      );
    }
    const deliveries = [...record.deliveries];
    deliveries[deliveryIndex] = {
      ...delivery,
      ...(durableBackup ? { durableBackup } : {}),
      disposition,
      completedAt: Date.now(),
    };
    return { ...record, state: "delivery_pending", deliveries };
  });
}

function assertAllDeliveriesTerminal(
  record: FlatJoinSplitV2JournalRecord,
): void {
  if (record.swapOutputSeed && !record.swapOutputCommitment) {
    throw new Error("Unrecovered swap output secrets cannot be removed");
  }
  if (record.outputNotes.length !== record.deliveries.length) {
    throw new Error(
      "Settlement output and delivery metadata are not in one-to-one correspondence",
    );
  }
  const outputCommitments = new Set(
    record.outputNotes.map((note) =>
      canonicalField(note.commitment, "output commitment"),
    ),
  );
  const deliveryCommitments = new Set<string>();
  for (const delivery of record.deliveries) {
    const commitment = canonicalField(
      delivery.commitment,
      "delivery commitment",
    );
    if (
      !outputCommitments.has(commitment) ||
      deliveryCommitments.has(commitment)
    ) {
      throw new Error(
        "Settlement output and delivery metadata are not in one-to-one correspondence",
      );
    }
    deliveryCommitments.add(commitment);
    if (
      (delivery.disposition !== "delivered" &&
        delivery.disposition !== "superseded") ||
      !Number.isSafeInteger(delivery.completedAt) ||
      (delivery.completedAt ?? 0) <= 0
    ) {
      throw new Error(
        "Settlement recovery data cannot be removed until every output delivery is terminal",
      );
    }
    if (
      delivery.disposition === "delivered" &&
      !delivery.encryptedNote &&
      !delivery.durableBackup
    ) {
      throw new Error(
        "Delivered settlement output is missing its staged envelope",
      );
    }
    if (delivery.encryptedNote) {
      canonicalEncryptedNoteEnvelope(delivery.encryptedNote);
    }
    if (delivery.durableBackup) {
      canonicalDeliveryBackupEvidence(delivery.durableBackup);
    }
  }
}

export async function removeFlatJoinSplitV2SettlementJournalRecord(
  scopeInput: FlatJoinSplitV2JournalScope,
  id: string,
): Promise<void> {
  const scope = canonicalScope(scopeInput);
  await withMutationLock(scope, async () => {
    const records = await readRecords(scope);
    const record = records.find((candidate) => candidate.id === id);
    if (!record) return;
    if (record.state !== "finalized" && record.state !== "delivery_pending") {
      throw new Error("Unfinalized settlement recovery data cannot be removed");
    }
    assertAllDeliveriesTerminal(record);
    if (isPrivateBridgeJournal(record)) {
      // A legacy random-ID completion must also stop calldata-based restores.
      // Use a separate marker so a sibling's recovery data is never overwritten.
      const completionId = bridgeCompletionId(record.transaction.data);
      await writePayload(scope, completionId, {
        type: "deleted",
        id: completionId,
      });
    }
    // The immutable remote PREPARED backup survives completion too. Retain an
    // authenticated, secret-free tombstone for every completed record so the
    // next restore poll cannot revive it and deliver the same outputs again.
    // A fresh browser without local storage can still restore the remote copy.
    await writePayload(scope, id, { type: "deleted", id });
  });
}

/**
 * Closes a withdrawal record that can never execute. The caller proved, at a
 * hash-pinned final block, that a confirmed transaction consumed exactly its
 * input nullifiers while carrying a different settlement (a retry or a fresh
 * proof). Nullifiers spend once, and a record without outputs holds no note
 * secret to recover, so it is tombstoned like a completed record: the
 * immutable remote prepared backup must not revive it.
 */
export async function retireSupersededFlatJoinSplitV2SettlementJournalRecord(
  scopeInput: FlatJoinSplitV2JournalScope,
  id: string,
): Promise<void> {
  const scope = canonicalScope(scopeInput);
  await withMutationLock(scope, async () => {
    const records = await readRecords(scope);
    const record = records.find((candidate) => candidate.id === id);
    if (!record) return;
    if (
      (record.kind !== "withdraw" && record.kind !== "withdraw_batch") ||
      record.state === "finalized" ||
      record.state === "delivery_pending" ||
      record.sourceNullifiers.length === 0 ||
      record.outputNotes.length > 0 ||
      record.deliveries.length > 0 ||
      record.swapOutputSeed !== undefined
    ) {
      throw new Error(
        "Only an output-free withdrawal can be retired as superseded",
      );
    }
    await writePayload(scope, id, { type: "deleted", id });
  });
}
